import React, { useRef, useState, useEffect } from "react";
import { FaPlay, FaPause } from "react-icons/fa";
import {
  ButtonWrapper,
  Container,
  PlayPauseButton,
  Slider,
  SliderRow,
  TimeText,
  Title,
} from "./style";

type AudioPlayerProps = {
  src: string;
  title?: string;
};

const AudioPlayer: React.FC<AudioPlayerProps> = ({ src, title }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateProgress = () => setProgress(audio.currentTime);
    const setAudioDuration = () => setDuration(audio.duration);
    const onEnd = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", updateProgress);
    audio.addEventListener("loadedmetadata", setAudioDuration);
    audio.addEventListener("ended", onEnd);

    return () => {
      audio.removeEventListener("timeupdate", updateProgress);
      audio.removeEventListener("loadedmetadata", setAudioDuration);
      audio.removeEventListener("ended", onEnd);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }

    setIsPlaying(!isPlaying);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setProgress(value);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
      .toString()
      .padStart(2, "0");
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  if (!src) return null;

  return (
    <Container>
      {title && <Title>{title}</Title>}

      <audio ref={audioRef} src={src} />

      <ButtonWrapper>
        <PlayPauseButton onClick={togglePlay}>
          {isPlaying ? <FaPause /> : <FaPlay />}
        </PlayPauseButton>
      </ButtonWrapper>

      <SliderRow>
        <TimeText>{formatTime(progress)}</TimeText>

        <Slider
          min={0}
          max={duration}
          step={0.1}
          value={progress}
          onChange={handleSliderChange}
          progressPercent={(progress / duration) * 100 || 0}
        />

        <TimeText>{formatTime(duration)}</TimeText>
      </SliderRow>
    </Container>
  );
};

export { AudioPlayer };
